<script setup lang="ts">
import { ref, provide, computed } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "./store/authStore";
import ToastHost from "./components/layouts/ToastHost.vue";
import ErrorBoundary from "./components/layouts/ErrorBoundary.vue";

const route = useRoute();

const authRoutes = ['Login', 'Signup', 'ForgotPassword', 'ResetPassword', 'ResetPasswordQuery', 'InviteLanding', 'Terms'];
// Treat the pre-resolution phase (route.name still undefined on a fresh load) as an
// auth page so the app chrome (BaseHeader) never flashes on public pages like /terms.
const isAuthPage = computed(() => !route.name || authRoutes.includes(route.name as string));

const mapRoutes = ['NewRequest', 'TrackOrder'];
const isMapPage = computed(() => mapRoutes.includes(route.name as string));

const store = useAuthStore();
const showVerifyBanner = computed(() => store.isAuthenticated && store.isEmailVerified === false);
const resendingSent = ref(false);
async function resendVerification() {
  try {
    await store.resendVerification();
    resendingSent.value = true;
  } catch {}
}
</script>


<template>
  <el-config-provider namespace="ep">
    <ToastHost />
    <template v-if="isAuthPage">
      <ErrorBoundary>
        <router-view></router-view>
      </ErrorBoundary>
    </template>
    <template v-else>
      <BaseHeader />
      <div v-if="showVerifyBanner" class="verify-banner">
        <span>Please verify your email to place orders.</span>
        <button v-if="!resendingSent" @click="resendVerification" class="verify-banner__btn">Resend email</button>
        <span v-else class="verify-banner__sent">Email sent!</span>
      </div>
      <div class="main-container" :class="isMapPage ? 'main-container--map' : 'main-container--page'">
        <ErrorBoundary>
          <router-view></router-view>
        </ErrorBoundary>
      </div>
    </template>
  </el-config-provider>
</template>

<style>
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}

#app {
  color: var(--ep-text-color-primary);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.main-container--map {
  position: relative;
  flex: 1;
  overflow: hidden;
  width: 100%;
}

.verify-banner {
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
  color: #92400e;
  font-size: 13px;
  padding: 8px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.verify-banner__btn {
  border: none;
  background: none;
  color: #0061eb;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  text-decoration: underline;
}
.verify-banner__sent { font-weight: 600; color: #166534; }

.main-container--page {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  background: white;
  scrollbar-gutter: stable;
}
</style>